import React, { Fragment } from "react";
import PropTypes from "prop-types";
import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useFilters,
  useExpanded,
  usePagination,
} from "react-table";
import { Table, Row, Col, Button, Input } from "reactstrap";
import { Filter, DefaultColumnFilter } from "./filters";

function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce(value => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <Col xs={12} sm={6} md={4} className="ms-auto mt-2 mt-sm-0">
      <div className="search-box mb-2">
        <div className="position-relative">
          <input
            onChange={e => {
              setValue(e.target.value);
              onChange(e.target.value);
            }}
            id="search-bar-0"
            type="text"
            className="form-control rounded-pill border ps-5 pe-5"
            placeholder={`Search records...`}
            value={value || ""}
          />
          <span className="position-absolute" 
                style={{ top: "55%", transform: "translateY(-50%)", left: "15px" }}>
            <i className="bx bx-search-alt fs-5 text-primary"></i>
          </span>
          {value && (
            <button 
              className="btn btn-link position-absolute text-decoration-none p-0"
              onClick={() => {
                setValue("");
                onChange("");
              }}
              style={{ right: "15px", top: "55%", transform: "translateY(-50%)", color: "#74788d" }}
            >
              <i className="bx bx-x fs-5"></i>
            </button>
          )}
        </div>
      </div>
    </Col>
  );
}

  const TableContainer = ({
    columns,
    data,
    isGlobalFilter,
    isAddOptions,
    isAddUserList,
    handleOrderClicks,
    handleUserClick,
    handleCustomerClick,
    isAddCustList,
    initialSortColumnId,
    initialSortDesc = true,
    customPageSize = 10,
    className,
    customPageSizeOptions = [10, 20, 50, 100],
    isLoading = false
  }) => {
    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      page,
      prepareRow,
      canPreviousPage,
      canNextPage,
      pageOptions,
      pageCount,
      gotoPage,
      nextPage,
      previousPage,
      setPageSize,
      state,
      preGlobalFilteredRows,
      setGlobalFilter,
      state: { pageIndex, pageSize },
    } = useTable(
      {
        columns,
        data,
        defaultColumn: { Filter: DefaultColumnFilter },
        initialState: { 
          pageIndex: 0, 
          pageSize: customPageSize,
          sortBy: [
            {
              id: initialSortColumnId || columns[0]?.accessor || 'id',
              desc: initialSortDesc
            },
          ],
        },
      },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  );

  const generateSortingIndicator = column => {
    if (column.disableSortBy) {
      return null;
    }
    
    return column.isSorted ? (
      <span className="ms-2">
        {column.isSortedDesc ? 
          <i className="bx bx-sort-down text-primary" /> : 
          <i className="bx bx-sort-up text-primary" />
        }
      </span>
    ) : (
      <span className="ms-2 text-muted">
        <i className="bx bx-sort" />
      </span>
    );
  };

  const onChangeInSelect = event => {
    setPageSize(Number(event.target.value));
  };

  const onChangeInInput = event => {
    const page = event.target.value ? Number(event.target.value) - 1 : 0;
    gotoPage(page);
  };
  return (
    <Fragment>
      <Row className="mb-3 align-items-center">
        <Col md={3} className="d-flex align-items-center">
          <div className="d-flex align-items-center">
            <label className="me-2 mb-0">Show</label>
            <select
              className="form-select shadow-none border-1"
              value={pageSize}
              onChange={onChangeInSelect}
              style={{ width: "auto", minWidth: "80px" }}
            >
              {customPageSizeOptions.map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
            <label className="ms-2 mb-0">entries</label>
          </div>
        </Col>
        
        {isGlobalFilter && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        )}
        
        {isAddOptions && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded  mb-2 me-2"
                onClick={handleOrderClicks}
              >
                <i className="mdi mdi-plus me-1" />
                Add New Order
              </Button>
            </div>
          </Col>
        )}
        {isAddUserList && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="primary"
                className="btn mb-2 me-2"
                onClick={handleUserClick}
              >
                <i className="mdi mdi-plus-circle-outline me-1" />
                Create New User
              </Button>
            </div>
          </Col>
        )}
        {isAddCustList && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded mb-2 me-2"
                onClick={handleCustomerClick}
              >
                <i className="mdi mdi-plus me-1" />
                New Customers
              </Button>
            </div>
          </Col>
        )}
      </Row>

      <div className="table-responsive react-table" style={{ overflow: 'auto', position: 'relative' }}>
        <Table bordered hover {...getTableProps()} className={className} style={{ minWidth: '100%' }}>
          <thead className="table-light table-nowrap">
            {headerGroups.map(headerGroup => (
              <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th key={column.id} style={{ minWidth: column.minWidth || '150px', whiteSpace: 'nowrap' }}>
                    <div className="mb-2" {...column.getSortByToggleProps()}>
                      {column.render("Header")}
                      {generateSortingIndicator(column)}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.length > 0 ? (
              page.map(row => {
                prepareRow(row);
                return (
                  <Fragment key={row.getRowProps().key}>
                    <tr>
                      {row.cells.map(cell => {
                        return (
                          <td key={cell.id} {...cell.getCellProps()} 
                              style={{ 
                                maxWidth: cell.column.maxWidth || '300px', 
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  </Fragment>
                );
              })
            ) : (
              <tr>
                <td 
                  colSpan={columns.length} 
                  className="text-center py-4 text-muted"
                >
                  {isLoading ?
                    <div className="d-flex flex-column align-items-center" >
                      <div className="spinner-border text-green mb-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="text-green mb-0 fw-medium">Loading records...</p>
                    </div>
                  :
                    <div className="d-flex flex-column align-items-center">
                      <i className="bx bx-folder-open fs-1 mb-2"></i>
                      <h5>No records found</h5>
                    </div>
                  }
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <Row className="justify-content-md-end justify-content-center align-items-center">
        <Col className="col-md-auto">
          <div className="d-flex gap-1">
            <Button
              color="primary"
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
            >
              {"<<"}
            </Button>
            <Button
              color="primary"
              onClick={previousPage}
              disabled={!canPreviousPage}
            >
              {"<"}
            </Button>
          </div>
        </Col>
        <Col className="col-md-auto d-none d-md-block">
          Page{" "}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </Col>
        <Col className="col-md-auto">
          <Input
            type="text"
            min={1}
            style={{ width: 70 }}
            max={pageOptions.length}
            defaultValue={pageIndex + 1}
            onChange={onChangeInInput}
          />
        </Col>

        <Col className="col-md-auto">
          <div className="d-flex gap-1">
            <Button color="primary" onClick={nextPage} disabled={!canNextPage}>
              {">"}
            </Button>
            <Button
              color="primary"
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
            >
              {">>"}
            </Button>
          </div>
        </Col>
      </Row>
    </Fragment>
  );
};

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default TableContainer;
